import type { SelectorDescriptor } from '@/config/shared/selector';

export type TextMatchOptions = Pick<
  SelectorDescriptor,
  'value' | 'exact' | 'caseSensitive'
>;

/** Normaliza espaços em branco (trim + colapsa runs). */
export function normalizeSelectorText(text: string): string {
  return text.trim().replace(/\s+/g, ' ');
}

function prepare(text: string, caseSensitive: boolean): string {
  const normalized = normalizeSelectorText(text);
  return caseSensitive ? normalized : normalized.toLowerCase();
}

/**
 * Compara texto bruto de um elemento (`textContent`, inclui descendentes).
 * Preferir `isTextMatchLeaf` em match de click/walk-up.
 */
export function elementMatchesText(
  element: Element,
  options: TextMatchOptions
): boolean {
  const raw = element.textContent;
  if (raw == null) return false;

  const caseSensitive = options.caseSensitive === true;
  const haystack = prepare(raw, caseSensitive);
  const needle = prepare(options.value, caseSensitive);
  if (!needle) return false;

  if (options.exact === true) {
    return haystack === needle;
  }

  return haystack.includes(needle);
}

/**
 * Match de texto no elemento “folha”: o próprio elemento bate e nenhum
 * descendente também bate. Evita que ancestrais (card/body) disparem só
 * porque contêm um botão `text:Comprar agora` no `textContent`.
 */
export function isTextMatchLeaf(
  element: Element,
  options: TextMatchOptions
): boolean {
  if (!elementMatchesText(element, options)) return false;

  const descendants = element.querySelectorAll('*');
  for (let i = 0; i < descendants.length; i += 1) {
    const child = descendants[i];
    if (child && elementMatchesText(child, options)) {
      return false;
    }
  }

  return true;
}

/**
 * Busca elementos cujo texto satisfaz o seletor.
 * Escopo opcional via `closest` (container CSS). SSR-safe: sem document → [].
 */
export function queryElementsByText(
  options: TextMatchOptions & { closest?: string },
  root?: ParentNode | null
): Element[] {
  if (typeof document === 'undefined') return [];

  const scope = root ?? document;

  if (options.closest) {
    try {
      const containers: Element[] = [];
      if (scope instanceof Element && scope.matches(options.closest)) {
        containers.push(scope);
      }
      containers.push(...Array.from(scope.querySelectorAll(options.closest)));

      const matched: Element[] = [];
      for (const container of containers) {
        matched.push(...collectTextMatches(container, options));
      }
      return Array.from(new Set(matched));
    } catch {
      return [];
    }
  }

  return collectTextMatches(scope, options);
}

function collectTextMatches(
  root: ParentNode,
  options: TextMatchOptions
): Element[] {
  const results: Element[] = [];
  const nodes = Array.from(root.querySelectorAll('*'));

  if (root instanceof Element) {
    nodes.unshift(root);
  }

  for (const el of nodes) {
    if (isTextMatchLeaf(el, options)) {
      results.push(el);
    }
  }

  return results;
}

export const TextSelectorResolver = {
  normalizeSelectorText,
  elementMatchesText,
  isTextMatchLeaf,
  queryElementsByText,
} as const;
